import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Paperclip, Trash2, Download, Upload, Save, Loader2, Users, X, Check, Package, AlertTriangle, Eye, FileImage, FileText as FileTextIcon, XCircle, History, Ticket, Unlink, Mail, Phone } from 'lucide-react';
import { format } from '../utils/dateUtils';
import { RichTextEditor, RichTextDisplay } from '../components/RichTextEditor';
import { getTicketTypeBadge } from './Tickets';

import config from '../config';
const API = `${config.BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'very-high', label: 'Very High', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'high', label: 'High', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800 border-gray-300' }
];

// Helper to check if file is previewable
const isPreviewable = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const previewableExtensions = [
    // Images
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'ico', 'bmp',
    // Documents
    'pdf',
    // Text and code
    'txt', 'md', 'html', 'htm', 'css', 'js', 'ts', 'json', 'xml', 'csv', 'yaml', 'yml',
    'py', 'java', 'c', 'cpp', 'h', 'sql', 'sh', 'bat', 'ps1', 'rb', 'php', 'go', 'rs', 'swift', 'kt',
    'env', 'log', 'ini', 'conf', 'cfg',
    // Audio
    'mp3', 'wav', 'ogg', 'm4a',
    // Video
    'mp4', 'webm', 'ogv'
  ];
  return previewableExtensions.includes(ext);
};

const isImage = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(ext);
};

const isAudio = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
};

const isVideo = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['mp4', 'webm', 'ogv'].includes(ext);
};

const isCode = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['js', 'ts', 'py', 'java', 'c', 'cpp', 'h', 'sql', 'sh', 'bat', 'ps1', 'rb', 'php', 'go', 'rs', 'swift', 'kt', 'json', 'xml', 'yaml', 'yml', 'css', 'html', 'htm'].includes(ext);
};

export const UserStoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [story, setStory] = useState(null);
  const [users, setUsers] = useState([]);
  const [releases, setReleases] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  
  // Comment state
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Close story state
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeStatus, setCloseStatus] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closing, setClosing] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    status: '',
    priority: 'medium',
    release_id: '',
    assigned_users: [],
    internal: false,
    customer: '',
    release_notes: ''
  });

  // Define useCallback functions BEFORE useEffect that uses them
  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user-stories/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  }, [id]);

  const fetchActivity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user-stories/${id}/activity`);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  }, [id]);

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user-stories/${id}/tickets`);
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    }
  }, [id]);

  const fetchStory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user-stories/${id}`);
      setStory(response.data);
      setForm({
        title: response.data.title,
        description: response.data.description,
        status: response.data.status,
        priority: response.data.priority || 'medium',
        release_id: response.data.release_id || '',
        assigned_users: response.data.assigned_users || [],
        internal: response.data.internal || false,
        customer: response.data.customer || '',
        release_notes: response.data.release_notes || ''
      });
      // Fetch releases for this project
      const releasesRes = await axios.get(`${API}/releases`, {
        params: { project_id: response.data.project_id }
      });
      setReleases(releasesRes.data);
    } catch (error) {
      console.error('Failed to fetch story:', error);
      toast.error('Failed to load user story');
      navigate('/user-stories');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/statuses`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Statuses offered when closing a story: "done" ones first, then
  // "rejected" ones - anything else (open/in-progress) isn't a valid
  // close target.
  const closeableStatuses = [
    ...statuses.filter((s) => s.is_done),
    ...statuses.filter((s) => s.is_rejected && !s.is_done),
  ];

  const openCloseDialog = () => {
    const defaultStatus = closeableStatuses.find((s) => s.key === story.status)?.key
      || closeableStatuses[0]?.key
      || '';
    setCloseStatus(defaultStatus);
    setCloseReason('');
    setCloseDialogOpen(true);
  };

  const handleCloseStory = async () => {
    if (!closeStatus) return;
    setClosing(true);
    try {
      await axios.post(`${API}/user-stories/${id}/close`, {
        status: closeStatus,
        reason: closeReason.trim() || null,
      });
      toast.success('User story closed');
      setCloseDialogOpen(false);
      fetchStory();
      fetchActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close user story');
    } finally {
      setClosing(false);
    }
  };

  useEffect(() => {
    fetchStory();
    fetchUsers();
    fetchStatuses();
    fetchComments();
    fetchActivity();
    fetchTickets();
  }, [id, fetchStory, fetchComments, fetchActivity, fetchTickets]);

  const handleUnlinkTicket = async (ticketId) => {
    try {
      await axios.delete(`${API}/tickets/${ticketId}/link`);
      toast.success('Ticket unlinked');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to unlink ticket');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/user-stories/${id}/comments`, { content: newComment });
      toast.success('Comment added');
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Backlog stories have no release: send null instead of an empty string
        release_id: form.release_id === '' || form.release_id === null ? null : parseInt(form.release_id),
      };
      await axios.put(`${API}/user-stories/${id}`, payload);
      toast.success('User story updated');
      fetchStory();
      fetchActivity();
    } catch (error) {
      toast.error('Failed to update user story');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Do NOT set Content-Type manually: axios sets multipart/form-data
      // with the correct boundary automatically for FormData.
      await axios.post(`${API}/user-stories/${id}/attachments`, formData);
      toast.success('Attachment uploaded');
      fetchStory();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to upload attachment: ${detail}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Upload all dropped files
      for (const file of files) {
        await uploadFile(file);
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`${API}/user-stories/${id}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchStory();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  // Download attachment with authentication
  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(`${API}/attachments/${attachment.filename}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  // Preview attachment with authentication
  const handlePreview = async (attachment) => {
    setPreviewFilename(attachment.original_filename);
    setPreviewLoading(true);
    setPreviewOpen(true);
    
    try {
      const response = await axios.get(`${API}/attachments/${attachment.filename}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      
      if (isImage(attachment.original_filename)) {
        // For images, create object URL
        const url = window.URL.createObjectURL(blob);
        setPreviewContent({ type: 'image', url });
      } else if (attachment.original_filename.endsWith('.pdf')) {
        // For PDFs, create object URL
        const url = window.URL.createObjectURL(blob);
        setPreviewContent({ type: 'pdf', url });
      } else if (isAudio(attachment.original_filename)) {
        // For audio files, create object URL
        const url = window.URL.createObjectURL(blob);
        setPreviewContent({ type: 'audio', url });
      } else if (isVideo(attachment.original_filename)) {
        // For video files, create object URL
        const url = window.URL.createObjectURL(blob);
        setPreviewContent({ type: 'video', url });
      } else if (isCode(attachment.original_filename)) {
        // For code files, read as text with syntax highlighting hint
        const text = await blob.text();
        const ext = attachment.original_filename.split('.').pop().toLowerCase();
        setPreviewContent({ type: 'code', content: text, language: ext });
      } else {
        // For text files, read as text
        const text = await blob.text();
        setPreviewContent({ type: 'text', content: text });
      }
    } catch (error) {
      toast.error('Failed to load preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleAssignee = (userId) => {
    setForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusInfo = statuses.find(s => s.key === status);
    if (statusInfo) {
      return (
        <Badge 
          variant="secondary" 
          className="gap-1"
          style={{ backgroundColor: statusInfo.color + '20', color: statusInfo.color, borderColor: statusInfo.color }}
        >
          <div 
            className="w-2 h-2 rounded-full" 
            style={{ backgroundColor: statusInfo.color }}
          />
          {statusInfo.name}
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        {status}
      </Badge>
    );
  };

  const statusDisplayName = (key) => statuses.find((s) => s.key === key)?.name || key;

  // Turn a raw audit log entry (action + details) into a human sentence
  // for the activity timeline.
  const describeActivity = (entry) => {
    const details = entry.details || {};
    switch (entry.action) {
      case 'create':
        return details.from_idea ? 'created this story from an idea' : 'created this story';
      case 'close': {
        const status = statusDisplayName(details.status);
        return details.close_reason
          ? `closed this story as "${status}" — ${details.close_reason}`
          : `closed this story as "${status}"`;
      }
      case 'update':
      case 'bulk_status_update':
      case 'bulk_assign': {
        const fieldLabels = {
          status: (v) => `changed status to "${statusDisplayName(v)}"`,
          priority: (v) => `changed priority to "${v}"`,
          title: () => 'renamed the story',
          description: () => 'updated the description',
          assigned_users: () => 'changed assigned users',
          release_id: () => 'changed the release',
          internal: (v) => (v ? 'marked as internal' : 'unmarked as internal'),
          customer: (v) => `set customer to "${v}"`,
          release_notes: () => 'updated the release notes',
        };
        const fragments = Object.entries(details)
          .map(([key, value]) => fieldLabels[key]?.(value) || `updated ${key}`);
        const prefix = entry.action.startsWith('bulk_') ? '(bulk) ' : '';
        return fragments.length > 0 ? `${prefix}${fragments.join(', ')}` : `${prefix}updated this story`;
      }
      default:
        return `${entry.action.replace(/_/g, ' ')} this story`;
    }
  };

  // Comments and audit log entries merged into one chronological feed.
  const timelineEntries = [
    ...comments.map((c) => ({ type: 'comment', timestamp: c.created_at, data: c })),
    ...activity.map((a) => ({ type: 'activity', timestamp: a.timestamp, data: a })),
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="space-y-6" data-testid="user-story-detail">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/user-stories')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="issue-number text-sm">{story.issue_number}</span>
            {getStatusBadge(story.status)}
            {story.release_version && (
              <Badge variant="outline" className="font-mono gap-1">
                <Package className="h-3 w-3" />
                v{story.release_version}
              </Badge>
            )}
          </div>
          <h1 className="text-2xl font-semibold mt-1">{story.title}</h1>
        </div>
        <Button
          variant="outline"
          onClick={openCloseDialog}
          disabled={closeableStatuses.length === 0}
          className="gap-2"
          data-testid="close-story-btn-top"
        >
          <XCircle className="h-4 w-4" />
          Close Story
        </Button>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-story-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="story-title-edit"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <RichTextEditor
                  id="description"
                  value={form.description}
                  onChange={(value) => setForm({ ...form, description: value })}
                  maxLength={5000}
                  data-testid="story-description-edit"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => setForm({ ...form, status: value })}
                  >
                    <SelectTrigger data-testid="story-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((status) => (
                        <SelectItem key={status.key} value={status.key}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: status.color }}
                            />
                            {status.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(value) => setForm({ ...form, priority: value })}
                  >
                    <SelectTrigger data-testid="story-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Release</Label>
                <Select
                  value={form.release_id ? String(form.release_id) : 'backlog'}
                  onValueChange={(value) => setForm({ ...form, release_id: value === 'backlog' ? '' : parseInt(value) })}
                >
                  <SelectTrigger data-testid="story-release-select">
                    <SelectValue placeholder="Select release" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="backlog" data-testid="release-option-backlog">Backlog (no release)</SelectItem>
                    {releases.map((release) => (
                      <SelectItem key={release.id} value={String(release.id)}>
                        v{release.version} - {release.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Customer</Label>
                <Input
                  value={form.customer}
                  onChange={(e) => setForm({ ...form, customer: e.target.value })}
                  placeholder="Customer or client name (optional)"
                  data-testid="story-customer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="story-release-notes">Release Notes</Label>
                <Textarea
                  id="story-release-notes"
                  value={form.release_notes}
                  onChange={(e) => setForm({ ...form, release_notes: e.target.value })}
                  placeholder="What needs to be done or communicated when releasing this story (optional)"
                  maxLength={5000}
                  rows={5}
                  data-testid="story-release-notes-input"
                />
                <p className="text-xs text-muted-foreground flex justify-between">
                  <span>Shown on the release report</span>
                  <span>{form.release_notes.length}/5000</span>
                </p>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-md mt-4">
                <div 
                  className={`h-5 w-5 rounded border flex items-center justify-center cursor-pointer ${form.internal ? 'bg-primary border-primary' : 'border-input'}`}
                  onClick={() => setForm({ ...form, internal: !form.internal })}
                  data-testid="story-internal-checkbox"
                >
                  {form.internal && <Check className="h-4 w-4 text-primary-foreground" />}
                </div>
                <div>
                  <Label className="cursor-pointer" onClick={() => setForm({ ...form, internal: !form.internal })}>
                    Internal Story
                  </Label>
                  <p className="text-xs text-muted-foreground">Internal stories are excluded from release reports</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tickets linked to this story */}
          {tickets.length > 0 && (
            <Card data-testid="story-tickets">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="h-5 w-5" />
                  Tickets ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="border rounded-md p-3 space-y-2" data-testid={`story-ticket-${ticket.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-muted-foreground">#{ticket.id}</span>
                      {getTicketTypeBadge(ticket.type)}
                      {getStatusBadge(ticket.status)}
                      <span className="text-sm font-medium">{ticket.customer_name}</span>
                      {ticket.app_name && <span className="text-sm text-muted-foreground">· {ticket.app_name}</span>}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-7 gap-1 text-muted-foreground"
                        onClick={() => handleUnlinkTicket(ticket.id)}
                        data-testid={`unlink-ticket-${ticket.id}`}
                      >
                        <Unlink className="h-3 w-3" />
                        Unlink
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                      {ticket.email && (
                        <a href={`mailto:${ticket.email}`} className="flex items-center gap-1 hover:text-primary">
                          <Mail className="h-3 w-3" />
                          {ticket.email}
                        </a>
                      )}
                      {ticket.phone && (
                        <a href={`tel:${ticket.phone}`} className="flex items-center gap-1 hover:text-primary">
                          <Phone className="h-3 w-3" />
                          {ticket.phone}
                        </a>
                      )}
                      <span>{format(new Date(ticket.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Activity - comments and audit history (status/assignment/field
              changes) merged into one chronological timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Activity ({timelineEntries.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add comment form */}
              <div className="space-y-2">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full min-h-[80px] p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="comment-input"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                    data-testid="submit-comment-btn"
                  >
                    {submittingComment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      'Post Comment'
                    )}
                  </Button>
                </div>
              </div>

              {/* Timeline */}
              {timelineEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No activity yet. Be the first to comment!
                </p>
              ) : (
                <div className="space-y-4 mt-4">
                  {timelineEntries.map((entry) =>
                    entry.type === 'comment' ? (
                      <div key={`comment-${entry.data.id}`} className="border rounded-lg p-4 space-y-2" data-testid={`comment-${entry.data.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                              {entry.data.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{entry.data.user_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(entry.data.created_at), 'MMM d, yyyy h:mm a')}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteComment(entry.data.id)}
                            data-testid={`delete-comment-${entry.data.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-sm whitespace-pre-wrap pl-10">{entry.data.content}</p>
                      </div>
                    ) : (
                      <div
                        key={`activity-${entry.data.id}`}
                        className="flex items-center gap-3 pl-1 text-sm text-muted-foreground"
                        data-testid={`activity-${entry.data.id}`}
                      >
                        <History className="h-4 w-4 flex-shrink-0" />
                        <span>
                          <span className="font-medium text-foreground">{entry.data.user_name}</span>{' '}
                          {describeActivity(entry.data)}
                        </span>
                        <span className="text-xs flex-shrink-0 ml-auto">
                          {format(new Date(entry.data.timestamp), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Assigned Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              {form.assigned_users.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {form.assigned_users.map(userId => (
                    <Badge key={userId} variant="secondary" className="gap-1 pr-1">
                      {getUserName(userId)}
                      <button
                        type="button"
                        onClick={() => toggleAssignee(userId)}
                        className="ml-1 rounded-full hover:bg-muted p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No users available</p>
                ) : (
                  <div className="p-2 space-y-1">
                    {users.map((user) => {
                      const isChecked = form.assigned_users.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                          onClick={() => toggleAssignee(user.id)}
                          data-testid={`assign-user-${user.id}`}
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center ${isChecked ? 'bg-primary border-primary' : 'border-input'}`}>
                            {isChecked && <Check className="h-3 w-3 text-primary-foreground" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium">{user.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">{user.email}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={isDragActive ? 'ring-2 ring-primary ring-offset-2' : ''}
          >
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Attachments
              </CardTitle>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-attachment-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isDragActive && (
                <div className="border-2 border-dashed border-primary rounded-lg p-8 mb-4 bg-primary/5 text-center">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-sm font-medium text-primary">Drop files here to upload</p>
                </div>
              )}
              {story.attachments?.length === 0 && !isDragActive ? (
                <div 
                  className="text-center py-8 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag and drop files here, or click to browse
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {story.attachments?.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                      data-testid={`attachment-${attachment.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {isImage(attachment.original_filename) ? (
                          <FileImage className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm font-medium">{attachment.original_filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(attachment.uploaded_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isPreviewable(attachment.original_filename) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handlePreview(attachment)}
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(attachment)}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          data-testid={`delete-attachment-${attachment.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {previewFilename}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 overflow-auto max-h-[70vh]">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : previewContent?.type === 'image' ? (
                <img 
                  src={previewContent.url} 
                  alt={previewFilename} 
                  className="max-w-full h-auto mx-auto rounded"
                />
              ) : previewContent?.type === 'pdf' ? (
                <iframe 
                  src={previewContent.url} 
                  className="w-full h-[60vh] rounded border"
                  title={previewFilename}
                />
              ) : previewContent?.type === 'audio' ? (
                <div className="flex items-center justify-center py-8">
                  <audio 
                    src={previewContent.url} 
                    controls 
                    className="w-full max-w-md"
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              ) : previewContent?.type === 'video' ? (
                <video 
                  src={previewContent.url} 
                  controls 
                  className="w-full max-h-[60vh] rounded"
                >
                  Your browser does not support the video element.
                </video>
              ) : previewContent?.type === 'code' ? (
                <pre className="bg-zinc-900 text-zinc-100 p-4 rounded text-sm overflow-auto font-mono">
                  <code>{previewContent.content}</code>
                </pre>
              ) : previewContent?.type === 'text' ? (
                <pre className="bg-muted p-4 rounded text-sm overflow-auto whitespace-pre-wrap">
                  {previewContent.content}
                </pre>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Project</Label>
                <p className="font-medium">{story.project_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Issue Number</Label>
                <p className="font-mono">{story.issue_number}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Release</Label>
                {story.release_id ? (
                  <p>
                    <Link 
                      to={`/releases/${story.release_id}`} 
                      className="font-medium hover:text-primary hover:underline"
                    >
                      v{story.release_version}
                    </Link>
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm">No release assigned</p>
                )}
              </div>
              {story.customer && (
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p className="font-medium">{story.customer}</p>
                </div>
              )}
              {story.close_reason && (
                <div>
                  <Label className="text-muted-foreground">Close Reason</Label>
                  <p className="whitespace-pre-wrap">{story.close_reason}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Created By</Label>
                <p>{story.created_by ? getUserName(story.created_by) : 'Unknown'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Created</Label>
                <p>{format(new Date(story.created_at), 'MMM d, yyyy h:mm a')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Assigned Users</Label>
                <p>{form.assigned_users.length > 0 ? form.assigned_users.length : 'None'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Attachments</Label>
                <p>{story.attachments?.length || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={openCloseDialog}
          disabled={closeableStatuses.length === 0}
          className="gap-2"
          data-testid="close-story-btn-bottom"
        >
          <XCircle className="h-4 w-4" />
          Close Story
        </Button>
      </div>

      {/* Close Story Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Close Story
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={closeStatus} onValueChange={setCloseStatus}>
                <SelectTrigger data-testid="close-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {closeableStatuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color }}
                        />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="close-reason">Reason (optional)</Label>
              <textarea
                id="close-reason"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="Mostly useful when rejecting a story..."
                className="w-full min-h-[80px] p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="close-reason-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCloseDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCloseStory}
                disabled={!closeStatus || closing}
                className="gap-2"
                data-testid="close-story-submit-btn"
              >
                {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Close Story
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
